import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navbar.css';

const sections = ['home', 'about', 'projects', 'skills', 'contact'];

const Navbar = ({ theme, toggleTheme }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);

      // Determine active section
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 200 && rect.bottom >= 200;
        }
        return false;
      }) || 'home';

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => {
      const next = !prev;
      document.body.style.overflow = next ? 'hidden' : '';
      return next;
    });
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    document.body.style.overflow = '';
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    closeMobileMenu();
    setActiveSection(sectionId);

    if (location.pathname !== '/') {
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const navbar = document.querySelector('.navbar-island');
      const navbarHeight = navbar ? navbar.offsetHeight : 70;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight - 16;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, [location.pathname, closeMobileMenu]);

  return (
    <header className={`navbar-header ${scrolled ? 'scrolled' : ''}`}>
      <nav className="navbar-island">
        {/* Brand Logo */}
        <div className="navbar-brand">
          <Link to="/" onClick={() => scrollToSection('home')} className="brand-logo">
            <span className="brand-dot"></span>
            Luigi<span className="brand-accent">.</span>
          </Link>
        </div>

        {/* Desktop Nav Links */}
        <ul className="nav-menu">
          {sections.map(section => {
            const label = section.charAt(0).toUpperCase() + section.slice(1);
            const isActive = location.pathname === '/' && activeSection === section;
            return (
              <li key={section} className="nav-item">
                <Link
                  to={location.pathname === '/' ? `#${section}` : '/'}
                  state={{ scrollToSection: section }}
                  className={`nav-link ${isActive ? 'active' : ''}`}
                  onClick={() => scrollToSection(section)}
                >
                  {label}
                  {isActive && <span className="active-pill"></span>}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Right Actions: Theme Toggle & Mobile Hamburger */}
        <div className="navbar-actions">
          <button
            className="theme-toggle-btn"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? (
              <svg className="theme-icon sun-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="4"></circle>
                <path d="M12 2v2"></path>
                <path d="M12 20v2"></path>
                <path d="m4.93 4.93 1.41 1.41"></path>
                <path d="m17.66 17.66 1.41 1.41"></path>
                <path d="M2 12h2"></path>
                <path d="M20 12h2"></path>
                <path d="m6.34 17.66-1.41 1.41"></path>
                <path d="m19.07 4.93-1.41 1.41"></path>
              </svg>
            ) : (
              <svg className="theme-icon moon-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"></path>
              </svg>
            )}
          </button>

          <button
            className={`hamburger-btn ${mobileMenuOpen ? 'open' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle Navigation Menu"
            aria-expanded={mobileMenuOpen}
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </div>
      </nav>

      {/* Mobile Drawer Menu */}
      <div className={`mobile-drawer ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-drawer-backdrop" onClick={closeMobileMenu}></div>
        <div className="mobile-drawer-content">
          <div className="mobile-drawer-header">
            <Link to="/" onClick={() => scrollToSection('home')} className="brand-logo">
              <span className="brand-dot"></span>
              Luigi<span className="brand-accent">.</span>
            </Link>
            <button className="mobile-close-btn" onClick={closeMobileMenu} aria-label="Close menu">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          <ul className="mobile-nav-list">
            {sections.map(section => {
              const label = section.charAt(0).toUpperCase() + section.slice(1);
              const isActive = location.pathname === '/' && activeSection === section;
              return (
                <li key={section} className="mobile-nav-item">
                  <Link
                    to={location.pathname === '/' ? `#${section}` : '/'}
                    state={{ scrollToSection: section }}
                    className={`mobile-nav-link ${isActive ? 'active' : ''}`}
                    onClick={() => scrollToSection(section)}
                  >
                    {label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </header>
  );
};

export default Navbar;